import { Key } from 'ts-keycode-enum';
import { IKeyListener } from './IKeyListener';
import { TtfFontResource } from './TtfFontResource';
import { TextSprite } from './TextSprite';
import { AbstractMouseEvent } from './AbstractMouseEvent';
import { TextSceneObject } from './TextSceneObject';
import { RenderDevice } from './RenderDevice';
import { Platform } from './Platform';
import { CGSKeyboardEvent } from './KeyboardEvent';
import { SceneObject } from './SceneObject';
import { Vector2 } from './Vector2';
import { TtfFontUtils } from './TtfFontUtils';
import { MouseDownEvent } from './MouseDownEvent';
import { Matrix3 } from './Matrix3';
import { SpriteBatch } from './SpriteBatch';
import { EventStreamSubscription, IStreamSubscription } from './EventStreamSubscription';
import { SceneObjectType } from './SceneObjectType';
import { EventStream } from './EventStreamSubscription';

import { Color4 } from './Color4';

export class TtfTextInputSceneObject extends TextSceneObject implements IKeyListener {
  get runtimeType(): SceneObjectType {
    return SceneObjectType.TtfTextEdit;
  }
  private _sprite: TextSprite | null = null;
  private _spriteWithCursor: TextSprite | null = null;

  public textChanged: EventStream<string> = new EventStream<string>();

  private readonly _renderDevice: RenderDevice;
  private readonly _fontResource: TtfFontResource;
  private readonly _platform: Platform;

  private _keyboardDownSubscription: EventStreamSubscription<CGSKeyboardEvent>;
  private _keyboardPressSubscription: EventStreamSubscription<CGSKeyboardEvent>;
  private _keyboardUpSubscription: EventStreamSubscription<CGSKeyboardEvent>;
  private readonly _canvas: HTMLCanvasElement;

  private _input: HTMLInputElement;
  private _placeHolderText: string;
  private _deviceLost: IStreamSubscription;
  private _activeSceneObject: SceneObject | null = null;

  private _cursorPosition: number = 0;
  get cursorPosition(): number {
    return this._cursorPosition;
  }

  set cursorPosition(value: number) {
    const newPosition = this._ensureCorrectCursorPosition(value);
    if (newPosition !== this._cursorPosition) {
      this._freeCursor();
    }
    this._cursorPosition = newPosition;
  }

  private _cursorTimer: number = 0.0;

  public removeListener(): void {
    this._canvas.focus();
    this._keyboardDownSubscription?.cancel();
    this._keyboardPressSubscription?.cancel();
    this._keyboardUpSubscription?.cancel();

    this._activeSceneObject?.stateMachine?.switchToState('disactive');
    this._platform.inputSystem.keyboard.removeListener(this);
  }

  public addListener(): void {
    //this.parent?.parent?.stateMachine?.switchToState('active');
    if (!this._keyboardDownSubscription) {
      this._keyboardDownSubscription = this._platform.inputSystem.keyboard.keyDown.listen((event) =>
        this._onKeyDown(event)
      );
    }
    if (!this._keyboardPressSubscription) {
      this._keyboardPressSubscription = this._platform.inputSystem.keyboard.keyPress.listen(
        (event) => this._onKeyPress(event)
      );
    }
    if (!this._keyboardUpSubscription) {
      this._keyboardUpSubscription = this._platform.inputSystem.keyboard.keyUp.listen((event) =>
        this._onKeyUp(event)
      );
    }

    this._activeSceneObject?.stateMachine?.switchToState('active');
    this._platform.inputSystem.keyboard.setActiveListener(this);
  }

  public setActiveElment(sceneObject: SceneObject): void {
    this._activeSceneObject = sceneObject;
  }

  private _ensureCorrectCursorPosition(value: number): number {
    return Math.max(0, Math.min(value, this.text.length));
  }

  constructor(
    renderDevice: RenderDevice,
    fontResource: TtfFontResource,
    platform: Platform,
    canvas: HTMLCanvasElement
  ) {
    super();
    this._renderDevice = renderDevice;
    this._fontResource = fontResource;
    this._platform = platform;
    this._canvas = canvas;
    this.touchable = true;
  }

  updateImpl(dt: number): void {
    if (this._platform.inputSystem.keyboard.activeListener === this) {
      this._cursorTimer += dt;
      if (this._cursorTimer > 1) {
        this._cursorTimer = this._cursorTimer - Math.trunc(this._cursorTimer);
      }
    }
    super.updateImpl(dt);
  }

  private _onKeyDown(event: CGSKeyboardEvent): void {
    this.onKeyDown(event);
  }
  private _onKeyPress(event: CGSKeyboardEvent): void {
    this.onKeyPress(event);
  }
  private _onKeyUp(event: CGSKeyboardEvent): void {
    this.onKeyUp(event);
  }

  drawImpl(spriteBatch: SpriteBatch, transform: Matrix3): void {
    if (this._platform.inputSystem.keyboard.activeListener === this && this._cursorTimer < 0.5) {
      this.buildCursor();
      const cursorSprite = this._spriteWithCursor!;
      spriteBatch.drawRect(cursorSprite.texture, cursorSprite.rect, cursorSprite.logicalRect, transform);
    } else {
      this.build();
      spriteBatch.drawRect(
        this._sprite!.texture,
        this._sprite!.rect,
        this._sprite!.logicalRect,
        transform
      );
    }

    if (!(this._platform.inputSystem.keyboard.activeListener === this)) {
      //this.parent?.parent?.stateMachine?.switchToState('disactive');
      this._activeSceneObject?.stateMachine?.switchToState('disactive');
    }
  }

  buildCursor(): void {
    if (!this._spriteWithCursor) {
      this._buildCursor();
    }
  }

  private _buildCursor(): void {
    const fontFamily = TtfFontUtils.getFontFamily(this._fontResource.id);
    const textToRender = this.textFormated || ' ';

    // Apply current effects to cursor rendering
    this._spriteWithCursor = this._fontResource.data!.build(
      this.textRenderParams,
      textToRender,
      this.size,
      fontFamily,
      this.fontSize,
      this.lineSpacing,
      this.halign,
      this.valign,
      false,
      this.cursorPosition
    );
  }

  build(): void {
    if (!this._sprite) {
      this._build();
    }
  }

  private _build(): void {
    const fontFamily = TtfFontUtils.getFontFamily(this._fontResource.id);

    this._sprite = this._fontResource.data!.build(
      this.textRenderParams,
      this.textFormated,
      this.size,
      fontFamily,
      this.fontSize,
      this.lineSpacing,
      this.halign,
      this.valign,
      false
    );
  }

  getLines(): string[] {
    return this._fontResource && this._fontResource.data ? this._fontResource.data.getLines() : [];
  }

  get maximumTextWidth(): number | null {
    return this._fontResource && this._fontResource.data
      ? this._fontResource.data.maximumTextWidth
      : null;
  }

  calculateTextWidth(text: string): number | null {
    return this._fontResource && this._fontResource.data
      ? this._fontResource.data.measureText(text)
      : null;
  }

  invalidate(): void {
    this._free();
    this._freeCursor();
  }

  initializeImpl(): void {
    this._deviceLost = this._renderDevice.lost.listen(() => this._onDeviceLost());
    if (!this.isInitialized) {
      this._input = document.createElement('input');
      this._input.type = 'text';
      this._input.className = 'overlay';
      this._input.style.position = 'absolute';
      //this._input.style.visibility = "hidden";
      this._input.style.top = '-400px';
      this._input.style.border = 'none';
      this._input.style.outline = 'none';
      this._input.style.backgroundColor = 'transparent';
      //this._input.style.color = 'transparent'; //!this.color ? Color4.White.asHtml : this.color.asHtml;
      this._input.style.color = !this.color ? Color4.White.asHtml : this.color.asHtml;
      this._input.spellcheck = false;
      this._input.enterKeyHint = 'done';
      this._input.maxLength = 9;
      this._input.minLength = 0;
      this._input.step = '1';
      this._input.oninput = () => {
        if (this._input.value.length > 8)
          this._input.value = this._input.value.slice(0, this._input.maxLength);
      };
      this._input.style.fontSize = `${this.fontSize}px`;
      this._input.value = this._placeHolderText || '';
      super.text = this._input.value;
      this._canvas.parentElement!.appendChild(this._input);
    }
  }

  deinitializeImpl(): void {
    this._input.remove();

    this._activeSceneObject?.stateMachine?.switchToState('disactive');
    //this.parent?.parent?.stateMachine?.switchToState('disactive');
    //this._platform.inputSystem.keyboard.removeListener(this);

    this._free();
    this._freeCursor();
    this._deviceLost.cancel();
  }

  activeChanged(active: boolean): void {
    this._platform.inputSystem.keyboard.removeListener(this);

    this._activeSceneObject?.stateMachine?.switchToState('disactive');
    //this.parent?.parent?.stateMachine?.switchToState('disactive');
    super.activeChanged(active);
  }

  private _free(): void {
    if (this._sprite) {
      this._fontResource.data!.dispose(this._sprite);
      this._sprite = null;
    }
  }

  private _freeCursor(): void {
    if (this._spriteWithCursor) {
      this._fontResource.data!.dispose(this._spriteWithCursor);
      this._spriteWithCursor = null;
    }
    this._resetCursorTimer();
  }

  private _onDeviceLost(): void {
    this._free();
    this._freeCursor();
  }

  onTouch(event: AbstractMouseEvent): void {
    this.handleMouseEvent(event);
  }

  handleMouseEvent(mouseEvent: AbstractMouseEvent): void {
    this._input.focus();
    if (mouseEvent instanceof MouseDownEvent) {
      if (this.isInPoint(mouseEvent.event.location)) {
        //this.parent?.parent?.stateMachine?.switchToState('active');
        this._activeSceneObject?.stateMachine?.switchToState('active');
        this._platform.inputSystem.keyboard.setActiveListener(this);
        const cursorAndLine = this._calcCursorPosition(mouseEvent.event.location);
        this.cursorPosition = cursorAndLine[0];
      }
    }
  }

  private _resetCursorTimer(): void {
    this._cursorTimer = 0.0;
  }

  private static worldPosition(sceneObject: SceneObject): Vector2 {
    let cur: SceneObject = sceneObject;
    let result: Vector2 = cur.position.clone();
    while (cur.parent) {
      cur = cur.parent;
      result = result.add(cur.position);
      result = result.multiply(cur.scale);
    }
    return result;
  }

  private _calcCursorPosition(touchLocation: Vector2): [number, number] {
    const local = this.inverseTransform.transformVector(touchLocation);

    const fontFamily = TtfFontUtils.getFontFamily(this._fontResource.id);
    return this._fontResource.data!.calcCursorPositionForPoint(
      local,
      this.textRenderParams,
      this.textFormated,
      this.size,
      fontFamily,
      this.fontSize,
      this.lineSpacing,
      this.halign,
      this.valign,
      false
    );
  }

  onKeyUp(_event: CGSKeyboardEvent): void {
    if (_event.keyCode == 0 || _event.keyCode == 229) {
      const keyCode = this._input.value.charCodeAt(this._input.value.length - 1);

      if ((keyCode >= 48 && keyCode <= 57) || keyCode === 46) {
        if (this.text.length <= 8) {
          this.text = this._input.value;
          this.cursorPosition++;
        }
        _event.accept();
      } else {
        this._input.value = this.text;
      }
      this.textChanged.dispatchEvent(this.text);
    }
  }

  onKeyPress(event: CGSKeyboardEvent): void {
    if ((event.charCode >= 48 && event.charCode <= 57) || event.charCode === 46) {
      const s = String.fromCharCode(event.charCode);
      if (this.text.length <= 8) {
        this.text = this.replaceRange(this.text, this.cursorPosition, this.cursorPosition, s);
        this.cursorPosition++;
        this.textChanged.dispatchEvent(this.text);
      }
      event.accept();
    } else {
      this._input.value = this._input.value.slice(0, this._input.value.length - 1);
    }
    this.textChanged.dispatchEvent(this.text);
    if (event.keyCode === Key.Enter) {
      this.removeListener();
      return;
    }
  }

  replaceRange(text: string, start: number, end: number, replacement: string): string {
    return text.substring(0, start) + replacement + text.substring(end);
  }

  onKeyDown(event: CGSKeyboardEvent): void {
    if (event.keyCode === Key.LeftArrow) {
      this.cursorPosition--;
      event.accept();
    } else if (event.keyCode === Key.RightArrow) {
      this.cursorPosition++;
      event.accept();
    } else if (event.keyCode === Key.Home) {
      this.cursorPosition = 0;
      event.accept();
    } else if (event.keyCode === Key.End) {
      this.cursorPosition = this.text.length;
      event.accept();
    } else if (event.keyCode === Key.Delete) {
      if (this.text.length > 0 && this.cursorPosition < this.text.length) {
        this.text = this.replaceRange(this.text, this.cursorPosition, this.cursorPosition + 1, '');
        event.accept();
      }
    } else if (event.keyCode === Key.Backspace) {
      if (this.text.length > 0 && this.cursorPosition > 0) {
        this.text = this.replaceRange(this.text, this.cursorPosition - 1, this.cursorPosition, '');
        this.cursorPosition--;
        event.accept();
      }
    }
  }
}
